import { useState, useCallback } from 'react'
import { addDocument } from '../services/firebase'
import { sendContactEmail, sendPrayerEmail, sendVisitEmail, sendRSVPEmail } from '../services/email'

// Generic form state hook
export function useFormState(initialState = {}) {
  const [values, setValues] = useState(initialState)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }, [])

  const handleBlur = useCallback((e) => {
    const { name } = e.target
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }, [])

  const setError = useCallback((name, error) => {
    setErrors(prev => ({ ...prev, [name]: error }))
  }, [])

  const reset = useCallback(() => {
    setValues(initialState)
    setErrors({})
    setTouched({})
  }, [initialState])

  const setFieldValues = useCallback((newValues) => {
    setValues(prev => ({ ...prev, ...newValues }))
  }, [])

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setValue,
    setError,
    setErrors,
    setFieldValues,
    reset,
  }
}

// Contact form submission hook
export function useContactForm() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (data) => {
    setSubmitting(true)
    setError(null)

    try {
      // Save to Firestore
      await addDocument('formSubmissions', {
        ...data,
        formType: 'contact',
        status: 'new',
        submittedAt: new Date().toISOString(),
      })

      // Send email notification (silently fail if email service not configured)
      try {
        await sendContactEmail(data)
      } catch (emailErr) {
        console.log('Email notification not sent:', emailErr)
      }

      setSubmitted(true)
      return { success: true }
    } catch (err) {
      console.error('Form submission error:', err)
      setError(err.message)
      return { success: false, error: err }
    } finally {
      setSubmitting(false)
    }
  }, [])

  const reset = useCallback(() => {
    setSubmitted(false)
    setError(null)
  }, [])

  return { submit, submitting, submitted, error, reset }
}

// Prayer request form submission hook
export function usePrayerForm() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (data) => {
    setSubmitting(true)
    setError(null)

    try {
      await addDocument('formSubmissions', {
        ...data,
        formType: 'prayer',
        status: 'new',
        submittedAt: new Date().toISOString(),
      })

      if (!data.isConfidential) {
        try {
          await sendPrayerEmail(data)
        } catch (emailErr) {
          console.log('Email notification not sent:', emailErr)
        }
      }

      setSubmitted(true)
      return { success: true }
    } catch (err) {
      console.error('Form submission error:', err)
      setError(err.message)
      return { success: false, error: err }
    } finally {
      setSubmitting(false)
    }
  }, [])

  const reset = useCallback(() => {
    setSubmitted(false)
    setError(null)
  }, [])

  return { submit, submitting, submitted, error, reset }
}

// Visit registration form hook
export function useVisitForm() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (data) => {
    setSubmitting(true)
    setError(null)

    try {
      await addDocument('formSubmissions', {
        ...data,
        formType: 'visit',
        status: 'new',
        submittedAt: new Date().toISOString(),
      })

      try {
        await sendVisitEmail(data)
      } catch (emailErr) {
        console.log('Email notification not sent:', emailErr)
      }

      setSubmitted(true)
      return { success: true }
    } catch (err) {
      console.error('Form submission error:', err)
      setError(err.message)
      return { success: false, error: err }
    } finally {
      setSubmitting(false)
    }
  }, [])

  const reset = useCallback(() => {
    setSubmitted(false)
    setError(null)
  }, [])

  return { submit, submitting, submitted, error, reset }
}

// Event RSVP hook
export function useRSVPForm(eventId) {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (data, eventDetails) => {
    setSubmitting(true)
    setError(null)

    try {
      await addDocument('formSubmissions', {
        ...data,
        formType: 'rsvp',
        eventId,
        eventName: eventDetails?.title,
        eventDate: eventDetails?.date,
        status: 'new',
        submittedAt: new Date().toISOString(),
      })

      try {
        await sendRSVPEmail({
          ...data,
          eventName: eventDetails?.title,
          eventDate: eventDetails?.date,
        })
      } catch (emailErr) {
        console.log('Email notification not sent:', emailErr)
      }

      setSubmitted(true)
      return { success: true }
    } catch (err) {
      console.error('Form submission error:', err)
      setError(err.message)
      return { success: false, error: err }
    } finally {
      setSubmitting(false)
    }
  }, [eventId])

  const reset = useCallback(() => {
    setSubmitted(false)
    setError(null)
  }, [])

  return { submit, submitting, submitted, error, reset }
}

// Newsletter signup hook
export function useNewsletterForm() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(null)

  const submit = useCallback(async (email, name = '') => {
    setSubmitting(true)
    setError(null)

    try {
      await addDocument('newsletter', {
        email,
        name,
        subscribedAt: new Date().toISOString(),
        active: true,
      })

      setSubmitted(true)
      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err }
    } finally {
      setSubmitting(false)
    }
  }, [])

  const reset = useCallback(() => {
    setSubmitted(false)
    setError(null)
  }, [])

  return { submit, submitting, submitted, error, reset }
}

export default useFormState
